/* Runtime auth configuration.
 *
 * Auth is toggled by VITE_AUTH_ENABLED (mirrors the VITE_ANALYTICS_API_BASE
 * mock-vs-real switch). Unset/"false" ⇒ auth is OFF: the session provider is
 * never mounted, RequireAuth passes everyone through, and the shell shows a
 * static dev user. Only gated deployments set VITE_AUTH_ENABLED="true". Local
 * dev, tests, Storybook, and algo environments leave it unset.
 *
 * All values come from window.__env__ (populated by env.ts from VITE_* vars) so
 * they resolve identically in dev, build, and tests. */

export interface FirebaseConfig {
  apiKey: string;
  appId: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  measurementId: string;
}

function readEnv(key: string): string | undefined {
  if (typeof window === "undefined") return undefined;

  const value = window.__env__?.[key];
  return value && value.length > 0 ? value : undefined;
}

/** True only when the deployment explicitly opts in to the auth gate. */
export const AUTH_ENABLED = readEnv("VITE_AUTH_ENABLED") === "true";

/** Gate email/password sign-up behind an invisible Firebase reCAPTCHA (mirrors
 *  studio's EXEC-2985). Off unless a gated deployment opts in via helm; the
 *  reCAPTCHA itself is configured on the shared Firebase project. */
export const CAPTCHA_ENABLED = readEnv("VITE_CAPTCHA_ENABLED") === "true";

/** Identity service base, e.g. "https://api.ai21-stg.com". */
export const IDENTITY_BASE_URL = readEnv("VITE_IDENTITY_BASE_URL");

/** Firebase config (see firebase.ts). Only read when auth is on. */
export const firebaseConfig: FirebaseConfig = {
  apiKey: readEnv("VITE_FIREBASE_API_KEY") ?? "",
  appId: readEnv("VITE_FIREBASE_APP_ID") ?? "",
  authDomain: readEnv("VITE_FIREBASE_AUTH_DOMAIN") ?? "",
  projectId: readEnv("VITE_FIREBASE_PROJECT_ID") ?? "",
  storageBucket: readEnv("VITE_FIREBASE_STORAGE_BUCKET") ?? "",
  messagingSenderId: readEnv("VITE_FIREBASE_MESSAGING_SENDER_ID") ?? "",
  measurementId: readEnv("VITE_FIREBASE_MEASUREMENT_ID") ?? "",
};
